import { BookingEventDateItem } from '../../Types/Event';

const MS_IN_DAY = 24 * 60 * 60 * 1000;

export type EventDateGroup = BookingEventDateItem[];

type GroupEventDatesOptions = {
  agencyModeActive?: boolean;
  sortGroupsBy?: 'start' | 'input';
};

export const getDayKey = (d: Date) => {
  const dt = new Date(d);
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`;
};

export const getEventDateGroupKey = (dates: EventDateGroup) => {
  const firstDate = dates[0];
  const lastDate = dates[dates.length - 1];

  if (dates.length === 1) {
    return firstDate.id;
  }

  return `${firstDate.eventId}-${getDayKey(firstDate.start)}-${getDayKey(lastDate.start)}`;
};

const getUtcDayStamp = (d: Date) => {
  const dt = new Date(d);
  return Date.UTC(dt.getFullYear(), dt.getMonth(), dt.getDate());
};

const canBeMerged = (current: BookingEventDateItem, previousInGroup: BookingEventDateItem) => {
  const currentDayStamp = getUtcDayStamp(current.start);
  const previousDayStamp = getUtcDayStamp(previousInGroup.start);
  const dayDifference = (currentDayStamp - previousDayStamp) / MS_IN_DAY;

  return dayDifference === 0 || dayDifference === 1;
};

export const groupEventDates = (
  eventDates: BookingEventDateItem[],
  { agencyModeActive = false, sortGroupsBy = 'start' }: GroupEventDatesOptions = {},
) => {
  const originalIndexes: Record<number, number> = {};
  eventDates.forEach((eventDate, index) => {
    originalIndexes[eventDate.id] = index;
  });

  const sortedDates = [...eventDates].sort((a, b) => new Date(a.start).getTime() - new Date(b.start).getTime());
  const groupsWithIndex: { dates: EventDateGroup; firstInputIndex: number }[] = [];
  const groupCandidatesByEventId: Record<number, BookingEventDateItem[]> = {};

  sortedDates.forEach((eventDate) => {
    if (eventDate.type !== 'REGULAR_DATE' || agencyModeActive) {
      groupsWithIndex.push({
        dates: [eventDate],
        firstInputIndex: originalIndexes[eventDate.id] ?? Number.MAX_SAFE_INTEGER,
      });
      return;
    }

    const existingEventDates = groupCandidatesByEventId[eventDate.eventId] ?? [];
    groupCandidatesByEventId[eventDate.eventId] = [...existingEventDates, eventDate];
  });

  Object.values(groupCandidatesByEventId).forEach((eventDatesOfEventId) => {
    const eventDatesSorted = [...eventDatesOfEventId].sort(
      (a, b) => new Date(a.start).getTime() - new Date(b.start).getTime(),
    );

    const eventGroups: EventDateGroup[] = [];
    eventDatesSorted.forEach((eventDate) => {
      const lastGroup = eventGroups[eventGroups.length - 1];
      if (!lastGroup || !canBeMerged(eventDate, lastGroup[lastGroup.length - 1])) {
        eventGroups.push([eventDate]);
        return;
      }
      lastGroup.push(eventDate);
    });

    groupsWithIndex.push(
      ...eventGroups.map((dates) => ({
        dates,
        firstInputIndex: Math.min(...dates.map((date) => originalIndexes[date.id] ?? Number.MAX_SAFE_INTEGER)),
      })),
    );
  });

  groupsWithIndex.sort((a, b) => {
    if (sortGroupsBy === 'input') {
      return a.firstInputIndex - b.firstInputIndex;
    }

    return new Date(a.dates[0].start).getTime() - new Date(b.dates[0].start).getTime();
  });

  return groupsWithIndex.map(({ dates }) => dates);
};
